import { useMemo } from 'react';
import useVAFeatureFlags from 'src/hooks/useVAFeatureFlags';
import { analyzePedigree } from './index';
import { structureHash } from './structureHash';
import { useDiseaseMeta } from './useDiseaseMeta';

// Runs the pure analyzer over the live pedigree config, memoised on the
// structure hash (+ carrier inputs) so unrelated UI state changes don't
// recompute. Gated by Clinical Insights — returns null when disabled. `members`
// is the validatePedigreeConfig output; `carrierStatusByMember` is the byMember
// map from the shipped deriveCarrierStatus (reused, not recomputed).
export function usePedigreeAnalysis(
  members,
  {
    carrierStatusByMember = {},
    profileMatch = null,
    conditionCarrierEnabled = false
  } = {}
) {
  const flags = useVAFeatureFlags();
  // Fail CLOSED while the flags load. `useVAFeatureFlags` returns null until the
  // fetch settles and a flags object afterwards — including on failure, where it
  // resolves to DEFAULT_FLAGS (clinicalInsightsEnabled: true). So null is a
  // transient loading state, never a terminal one, and gating on it cannot strand
  // the panel off; it only stops us running and rendering clinical conclusions
  // for a deployment that may have Clinical Insights switched off
  // (TGCLOUD-1712 PA-8 — was `flags ? ... : true`, i.e. analysis ran during load).
  const enabled = Boolean(flags) && flags.clinicalInsightsEnabled !== false;

  // inheritanceCandidateGate: whether a Mendelian mode may only be asserted
  // where the disease's own reference data supports one (TGCLOUD-1712 DR-1).
  const inheritanceGateEnabled = Boolean(flags?.inheritanceCandidateGate);

  // Reference metadata for the pedigree's diseases. Only fetched when the gate
  // is actually on — with it off the analyzer ignores the metadata entirely, so
  // requesting it would be a network call for nothing.
  const { diseaseMeta, loading: metaLoading } = useDiseaseMeta(members, {
    enabled: enabled && inheritanceGateEnabled
  });

  const hash = useMemo(
    () => (enabled ? structureHash(members || []) : ''),
    [members, enabled]
  );
  const carrierKey = useMemo(
    () => JSON.stringify(carrierStatusByMember || {}),
    [carrierStatusByMember]
  );

  return useMemo(() => {
    if (!enabled) return null;
    // Hold off while the disease metadata is in flight, but ONLY when the gate
    // is on. Running without it would assert modes for a moment and then
    // withdraw them as the response lands — a panel that changes its clinical
    // claim under the reader is worse than one that arrives a beat later.
    if (inheritanceGateEnabled && metaLoading) return null;
    return analyzePedigree(members || [], {
      carrierStatusByMember,
      profileMatch,
      conditionCarrierEnabled,
      diseaseMeta,
      inheritanceGateEnabled
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hash,
    carrierKey,
    enabled,
    profileMatch,
    conditionCarrierEnabled,
    inheritanceGateEnabled,
    diseaseMeta,
    metaLoading
  ]);
}
